import { useState } from "react";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/24/outline";

export function CustomDropdown({
  value,
  onChange,
  options,
  placeholder,
  className = "",
  disabled = false,
  loading = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find((option) => option.value === value);

  const handleSelect = (optionValue) => {
    if (disabled || loading) return;
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (disabled || loading) return;
    setIsOpen(!isOpen);
  };

  const getColorClasses = (color, isSelected) => {
    if (!color) return "";

    const colorMap = {
      blue: isSelected
        ? "bg-blue-50 text-blue-600"
        : "text-blue-600 hover:bg-blue-50",
      red: isSelected
        ? "bg-red-50 text-red-600"
        : "text-red-600 hover:bg-red-50",
      green: isSelected
        ? "bg-green-50 text-green-600"
        : "text-green-600 hover:bg-green-50",
      gray: isSelected
        ? "bg-gray-50 text-gray-600"
        : "text-gray-600 hover:bg-gray-50",
      yellow: isSelected
        ? "bg-yellow-50 text-yellow-600"
        : "text-yellow-600 hover:bg-yellow-50",
      purple: isSelected
        ? "bg-purple-50 text-purple-600"
        : "text-purple-600 hover:bg-purple-50",
    };

    // Handle hex color #9a3412 (brown/orange)
    if (color === "#9a3412") {
      return isSelected
        ? "bg-orange-50 text-[#9a3412]"
        : "text-[#9a3412] hover:bg-orange-50";
    }

    return colorMap[color] || "";
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled || loading}
        className={`w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-left text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-slate-300 transition-colors ${
          disabled || loading ? "opacity-50 cursor-not-allowed" : ""
        } ${className}`}
      >
        <div className="flex items-center justify-between">
          <span className={selectedOption ? "text-slate-900" : "text-slate-400"}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          {loading ? (
            <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
          ) : (
            <ChevronDownIcon
              className={`w-4 h-4 text-slate-400 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          )}
        </div>
      </button>

      {isOpen && !loading && !disabled && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Menu */}
          <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-auto">
            {options.map((option, index) => (
              <div key={option.value}>
                {/* Show divider before this option if divider is true */}
                {option.divider && index > 0 && (
                  <div className="border-t border-gray-300 my-1"></div>
                )}
                <button
                  type="button"
                  onClick={() => !option.disabled && handleSelect(option.value)}
                  disabled={option.disabled}
                  className={`w-full px-4 py-2 text-left text-sm focus:outline-none first:rounded-t-xl last:rounded-b-xl transition-colors ${
                    option.disabled
                      ? "text-slate-500 font-bold cursor-default bg-slate-100"
                      : value === option.value
                      ? getColorClasses(option.color, true) ||
                        "bg-blue-50 text-blue-600"
                      : getColorClasses(option.color, false) ||
                        "text-slate-900 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className={option.disabled ? "font-bold" : "font-medium"}>{option.label}</span>
                    {value === option.value && !option.disabled && (
                      <CheckIcon className="w-4 h-4" />
                    )}
                  </div>
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default CustomDropdown;
